import {
	type AccreditationSummary,
	type CardsSummary,
	type EntryOrganizationsResponse,
	type PrintBatchesPage,
	type QuotaUsage,
	type AccommodationSummary,
	REPORT_ROWS_MAX,
	formatAccreditationNo,
	formatRegistrationNo,
	formatTicketNo,
} from "@seagames/contracts";
import { api } from "../../lib/api/client";
import { fetchReportAthletesBySport, fetchReportCards, fetchReportPeople, fetchReportRooming, fetchReportTickets } from "../../lib/api/reports";
import { asOfLabel, filterLine, reportDate, reportDateTime } from "./pdf/labels";
import type { TableReportSpec } from "./pdf/table-report";

/**
 * The Central Report Engine's catalogue (SUKMA's module pages, user
 * 2026-11-02). One entry per module card on the Overview; each report says
 * which filters it needs and builds its PDF spec from ONE rows request
 * (`/reports/*`) plus, where it prints counts, the module's own aggregate.
 *
 * What a PDF prints is ENGLISH (see `pdf/styles.ts`); what the page shows
 * is translated (`reports` namespace, keyed by module / report code).
 */

export const REPORT_MODULE_KEYS = ["accreditation", "eAccreditation", "entries", "accommodation", "helpdesk"] as const;
export type ReportModuleKey = (typeof REPORT_MODULE_KEYS)[number];

/** URL slug ↔ module key. */
export const REPORT_MODULE_SLUGS: Record<ReportModuleKey, string> = {
	accreditation: "accreditation",
	eAccreditation: "e-accreditation",
	entries: "entries",
	accommodation: "accommodation",
	helpdesk: "helpdesk",
};

export const REPORT_FILTER_KEYS = ["org", "status", "cardState", "ticketStatus", "category"] as const;
export type ReportFilterKey = (typeof REPORT_FILTER_KEYS)[number];
export type ReportFilters = Partial<Record<ReportFilterKey, string>>;

export interface ReportContext {
	gamesId: string;
	filters: ReportFilters;
	/** The picked organisation's name (for the subtitle), when one is picked. */
	orgName: string | null;
	now: Date;
	/** Aborted when the preview closes: the rows request and the render stop. */
	signal: AbortSignal;
}

export interface ReportDef {
	/** SUKMA's report code, printed in the footer; also the i18n key. */
	code: string;
	/** English, as printed. */
	pdfTitle: string;
	orientation: "portrait" | "landscape";
	/** The report cannot run without an organisation (an admin must pick one; a NOC's is implied). */
	needsOrg?: boolean;
	/** Admins only (the API's own fence agrees). */
	adminOnly?: boolean;
	build: (ctx: ReportContext) => Promise<TableReportSpec<never>>;
}

export interface ReportModuleDef {
	key: ReportModuleKey;
	/** English, the PDF's header line 1. */
	pdfTitle: string;
	/** Which filters its page shows. */
	filters: readonly ReportFilterKey[];
	adminOnly?: boolean;
	reports: readonly ReportDef[];
}

// ── helpers ────────────────────────────────────────────────────────────────

const human = (v: string | null | undefined): string => (v ? v.charAt(0) + v.slice(1).toLowerCase().replace(/_/g, " ") : "-");
const genderShort = (g: string | null | undefined) => (g === "MALE" ? "M" : g === "FEMALE" ? "W" : "-");

function ageOn(dob: string | null | undefined, now: Date): string {
	if (!dob) return "-";
	const [y, m, d] = dob.split("-").map(Number);
	if (!y || !m || !d) return "-";
	let age = now.getFullYear() - y;
	if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < d)) age -= 1;
	return String(age);
}

const orgLabel = (o: { code: string | null; name: string } | null | undefined) => (o ? (o.code ? `${o.code} - ${o.name}` : o.name) : "-");

function truncatedNotice(truncated: boolean): string | null {
	return truncated ? `Only the first ${REPORT_ROWS_MAX.toLocaleString("en-GB")} rows are printed. Narrow the filters for the rest.` : null;
}

function subtitles(ctx: ReportContext, extra: readonly (readonly [string, string | null | undefined])[] = []) {
	return [asOfLabel(ctx.now), ctx.orgName ? `Organisation: ${ctx.orgName}` : null, filterLine(extra)];
}

/** Stable sort by a text key — the rows arrive name-ordered from SQL; grouping keeps that order inside a group. */
function groupedBy<T>(rows: readonly T[], key: (r: T) => string): T[] {
	return rows
		.map((r, i) => [r, i] as const)
		.sort(([a, ia], [b, ib]) => key(a).localeCompare(key(b)) || ia - ib)
		.map(([r]) => r);
}

const sum = (xs: readonly number[]) => xs.reduce((a, b) => a + b, 0);

// The spec is built per Row type and erased at the catalogue boundary.
const spec = <Row>(s: TableReportSpec<Row>) => s as unknown as TableReportSpec<never>;

// ── Accreditation ──────────────────────────────────────────────────────────

const accreditation: ReportModuleDef = {
	key: "accreditation",
	pdfTitle: "Accreditation",
	filters: ["org", "status"],
	reports: [
		{
			code: "ACC_1.1",
			pdfTitle: "Entry List by Name",
			orientation: "landscape",
			async build(ctx) {
				const { items, truncated } = await fetchReportPeople({ games_id: ctx.gamesId, responsible_organization_id: ctx.filters.org, status: ctx.filters.status as never }, ctx.signal);
				const rows = ctx.filters.org ? items : groupedBy(items, (r) => (r.responsible_organization.code ? `0${r.responsible_organization.code}` : `1${r.responsible_organization.name}`));
				return spec({
					moduleTitle: "Accreditation",
					reportTitle: "Entry List by Name",
					code: "ACC_1.1",
					orientation: "landscape",
					subtitles: subtitles(ctx, [["Status", human(ctx.filters.status)]].filter(([, v]) => v !== "-") as never),
					notice: truncatedNotice(truncated),
					summary: [{ label: "Listed", value: rows.length }],
					columns: [
						{ header: "Accr. No", widthPct: 11, cell: (r) => r.accreditation_no_display ?? r.registration_no_display },
						{ header: "Name", widthPct: 18, cell: (r) => r.person.printed_name },
						{ header: "Org.", widthPct: 6, align: "center", cell: (r) => r.responsible_organization.code ?? "GMS" },
						{ header: "Function", widthPct: 15, cell: (r) => [r.main_function.code, r.sub_function?.name].filter(Boolean).join(" / ") },
						{ header: "ID / Passport No", widthPct: 12, cell: (r) => r.identification_number },
						{ header: "Gender", widthPct: 6, align: "center", cell: (r) => genderShort(r.person.gender) },
						{ header: "D.O.B", widthPct: 8, align: "center", cell: (r) => reportDate(r.person.date_of_birth) },
						{ header: "Age", widthPct: 4, align: "center", cell: (r) => ageOn(r.person.date_of_birth, ctx.now) },
						{ header: "Sport", widthPct: 11, cell: (r) => r.sport?.name },
						{ header: "Status", cell: (r) => human(r.status) },
					],
					rows,
					groupKey: ctx.filters.org ? undefined : (r) => orgLabel(r.responsible_organization),
					groupLabel: (k) => `Organisation: ${k}`,
					numbered: true,
					emptyText: "No records match these filters.",
				});
			},
		},
		{
			code: "ACC_1.3",
			pdfTitle: "Contingent List by Function",
			orientation: "portrait",
			needsOrg: true,
			async build(ctx) {
				const q = { games_id: ctx.gamesId, responsible_organization_id: ctx.filters.org, status: ctx.filters.status as never };
				const [{ items, truncated }, summary] = await Promise.all([fetchReportPeople(q, ctx.signal), api.get<AccreditationSummary>("/accreditations/summary", { query: Object.fromEntries(Object.entries(q).filter(([, v]) => v)) as Record<string, string>, signal: ctx.signal })]);
				const rows = groupedBy(items, (r) => r.main_function.code);
				return spec({
					moduleTitle: "Accreditation",
					reportTitle: "Contingent List by Function",
					code: "ACC_1.3",
					orientation: "portrait",
					subtitles: subtitles(ctx),
					notice: truncatedNotice(truncated),
					summary: [...summary.by_main_function.map((f) => ({ label: f.main_function.code, value: f.count })), { label: "Total", value: sum(summary.by_main_function.map((f) => f.count)) }],
					columns: [
						{ header: "Name", widthPct: 28, cell: (r) => r.person.printed_name },
						{ header: "Gender", widthPct: 8, align: "center", cell: (r) => genderShort(r.person.gender) },
						{ header: "D.O.B", widthPct: 12, align: "center", cell: (r) => reportDate(r.person.date_of_birth) },
						{ header: "Age", widthPct: 6, align: "center", cell: (r) => ageOn(r.person.date_of_birth, ctx.now) },
						{ header: "Sub Function", widthPct: 18, cell: (r) => r.sub_function?.name },
						{ header: "Sport", widthPct: 16, cell: (r) => r.sport?.name },
						{ header: "Status", cell: (r) => human(r.status) },
					],
					rows,
					groupKey: (r) => `${r.main_function.code} - ${r.main_function.name}`,
					groupLabel: (k) => `Function: ${k}`,
					numbered: true,
					emptyText: "No records match these filters.",
				});
			},
		},
		{
			code: "ACC_1.2",
			pdfTitle: "Number of Athletes by Sport",
			orientation: "portrait",
			async build(ctx) {
				const { items } = await fetchReportAthletesBySport({ games_id: ctx.gamesId, responsible_organization_id: ctx.filters.org }, ctx.signal);
				return spec({
					moduleTitle: "Accreditation",
					reportTitle: "Number of Athletes by Sport",
					code: "ACC_1.2",
					orientation: "portrait",
					subtitles: [...subtitles(ctx), "Approved athletes. An athlete in several sports counts once in each."],
					summary: [
						{ label: "Sports", value: new Set(items.map((r) => r.sport.id)).size },
						{ label: "Organisations", value: new Set(items.map((r) => r.organization.id)).size },
						{ label: "Women", value: sum(items.map((r) => r.women)) },
						{ label: "Men", value: sum(items.map((r) => r.men)) },
						{ label: "Sport entries", value: sum(items.map((r) => r.total)) },
					],
					columns: [
						{ header: "Organisation", cell: (r) => orgLabel(r.organization) },
						{ header: "Women", widthPct: 13, align: "center", cell: (r) => r.women },
						{ header: "Men", widthPct: 13, align: "center", cell: (r) => r.men },
						{ header: "Not given", widthPct: 13, align: "center", cell: (r) => r.unknown },
						{ header: "Total", widthPct: 13, align: "center", cell: (r) => r.total },
					],
					rows: items,
					groupKey: (r) => `${r.sport.code} - ${r.sport.name}`,
					groupLabel: (k) => k,
					totals: ["Total", sum(items.map((r) => r.women)), sum(items.map((r) => r.men)), sum(items.map((r) => r.unknown)), sum(items.map((r) => r.total))],
					emptyText: "No approved athletes yet.",
				});
			},
		},
		{
			code: "ACC_FQC",
			pdfTitle: "Function Quota by Contingent",
			orientation: "portrait",
			needsOrg: true,
			async build(ctx) {
				const usage = await api.get<QuotaUsage>("/quotas/usage", { query: { games_id: ctx.gamesId, ...(ctx.filters.org ? { responsible_organization_id: ctx.filters.org } : {}) }, signal: ctx.signal });
				type Line = { label: string; used: number; quota: number | null; women: number | null; men: number | null; sub: boolean };
				const rows: Line[] = usage.by_function.flatMap((f) => [
					{ label: `${f.main_function.code} - ${f.main_function.name}`, used: f.used, quota: f.quota, women: f.gender?.women ?? null, men: f.gender?.men ?? null, sub: false },
					...f.sub_functions.map((s) => ({ label: `    ${s.sub_function?.name ?? "(no sub function)"}`, used: s.used, quota: null, women: null, men: null, sub: true })),
				]);
				return spec<Line>({
					moduleTitle: "Accreditation",
					reportTitle: "Function Quota by Contingent",
					code: "ACC_FQC",
					orientation: "portrait",
					subtitles: subtitles(ctx),
					summary: [
						{ label: "Registered", value: usage.total.used },
						{ label: "Quota", value: usage.total.quota ?? "-" },
						{ label: "Rules not met", value: usage.violations.length },
					],
					columns: [
						{ header: "Function", cell: (r) => r.label },
						{ header: "Registered", widthPct: 14, align: "center", cell: (r) => r.used },
						{ header: "Quota", widthPct: 14, align: "center", cell: (r) => (r.sub ? "" : (r.quota ?? "-")) },
						{ header: "Women", widthPct: 12, align: "center", cell: (r) => (r.sub ? "" : (r.women ?? "-")) },
						{ header: "Men", widthPct: 12, align: "center", cell: (r) => (r.sub ? "" : (r.men ?? "-")) },
					],
					rows,
					totals: ["Total", usage.total.used, usage.total.quota ?? "-", "", ""],
					emptyText: "No quotas or registrations yet.",
				});
			},
		},
	],
};

// ── E-Accreditation (cards and printing) ───────────────────────────────────

type CardRow = Awaited<ReturnType<typeof fetchReportCards>>["items"][number];

const CARD_COLUMNS: TableReportSpec<CardRow>["columns"] = [
	{ header: "Card No", widthPct: 13, cell: (r) => r.accreditation_no_display ?? r.registration_no_display },
	{ header: "Name", widthPct: 22, cell: (r) => r.printed_name },
	{ header: "Org.", widthPct: 7, align: "center", cell: (r) => r.organization.code ?? "GMS" },
	{ header: "Category", widthPct: 12, cell: (r) => r.category.code },
	{ header: "Sport", widthPct: 13, cell: (r) => r.sport?.name },
	{ header: "Card State", widthPct: 11, cell: (r) => human(r.card_state) },
	{ header: "Printed", widthPct: 11, align: "center", cell: (r) => reportDateTime(r.printed_at) },
	{ header: "Valid Until", cell: (r) => (r.valid_until ? reportDate(r.valid_until) : "-") },
];

const eAccreditation: ReportModuleDef = {
	key: "eAccreditation",
	pdfTitle: "E-Accreditation",
	filters: ["org", "cardState"],
	reports: [
		{
			code: "EAC_1.1",
			pdfTitle: "Card Management",
			orientation: "landscape",
			async build(ctx) {
				const { items, truncated } = await fetchReportCards({ games_id: ctx.gamesId, responsible_organization_id: ctx.filters.org, card_state: ctx.filters.cardState as never }, ctx.signal);
				return spec<CardRow>({
					moduleTitle: "E-Accreditation",
					reportTitle: "Card Management",
					code: "EAC_1.1",
					orientation: "landscape",
					subtitles: subtitles(ctx, [["Card state", ctx.filters.cardState ? human(ctx.filters.cardState) : null]]),
					notice: truncatedNotice(truncated),
					summary: [{ label: "Cards", value: items.length }],
					columns: CARD_COLUMNS,
					rows: items,
					numbered: true,
					emptyText: "No cards match these filters.",
				});
			},
		},
		{
			code: "ACP_1.1",
			pdfTitle: "Print Status List",
			orientation: "landscape",
			async build(ctx) {
				const [{ items, truncated }, summary] = await Promise.all([
					fetchReportCards({ games_id: ctx.gamesId, responsible_organization_id: ctx.filters.org, card_state: ctx.filters.cardState as never }, ctx.signal),
					api.get<CardsSummary>("/cards/summary", { query: { games_id: ctx.gamesId, ...(ctx.filters.org ? { responsible_organization_id: ctx.filters.org } : {}) }, signal: ctx.signal }),
				]);
				const order = summary.by_state.map((s) => s.state as string);
				const rows = [...items].sort((a, b) => order.indexOf(a.card_state) - order.indexOf(b.card_state));
				return spec<CardRow>({
					moduleTitle: "AC Card Printing",
					reportTitle: "Print Status List",
					code: "ACP_1.1",
					orientation: "landscape",
					subtitles: subtitles(ctx, [["Card state", ctx.filters.cardState ? human(ctx.filters.cardState) : null]]),
					notice: truncatedNotice(truncated),
					summary: [{ label: "Approved cards", value: summary.total }, ...summary.by_state.filter((s) => s.count > 0).map((s) => ({ label: human(s.state), value: s.count }))],
					columns: CARD_COLUMNS,
					rows,
					groupKey: (r) => human(r.card_state),
					groupLabel: (k) => `Card state: ${k}`,
					numbered: true,
					emptyText: "No cards match these filters.",
				});
			},
		},
		{
			code: "ACP_1.2",
			pdfTitle: "Print Batches Summary",
			orientation: "landscape",
			adminOnly: true,
			async build(ctx) {
				// Batches are few (one per print run); walk the keyset pages, bounded.
				const items: PrintBatchesPage["items"] = [];
				let cursor: string | undefined;
				for (let i = 0; i < 20; i++) {
					const page = await api.get<PrintBatchesPage>("/print-batches", { query: { games_id: ctx.gamesId, limit: "100", ...(cursor ? { cursor } : {}) }, signal: ctx.signal });
					items.push(...page.items);
					if (!page.next_cursor) break;
					cursor = page.next_cursor;
				}
				type Batch = (typeof items)[number];
				return spec<Batch>({
					moduleTitle: "AC Card Printing",
					reportTitle: "Print Batches Summary",
					code: "ACP_1.2",
					orientation: "landscape",
					subtitles: [asOfLabel(ctx.now)],
					summary: [
						{ label: "Batches", value: items.length },
						{ label: "Cards", value: sum(items.map((b) => b.card_count)) },
						{ label: "Printed", value: items.filter((b) => b.status === "PRINTED").length },
					],
					columns: [
						{ header: "Batch", widthPct: 22, cell: (b) => b.name },
						{ header: "Kind", widthPct: 9, cell: (b) => human(b.kind) },
						{ header: "Organisation", widthPct: 17, cell: (b) => (b.organization ? orgLabel(b.organization) : "Several") },
						{ header: "Status", widthPct: 10, cell: (b) => human(b.status) },
						{ header: "Cards", widthPct: 7, align: "center", cell: (b) => b.card_count },
						{ header: "Created", widthPct: 12, align: "center", cell: (b) => reportDateTime(b.created_at) },
						{ header: "Generated", widthPct: 12, align: "center", cell: (b) => reportDateTime(b.generated_at) },
						{ header: "Printed", align: "center", cell: (b) => reportDateTime(b.printed_at) },
					],
					rows: items,
					numbered: true,
					emptyText: "No print batches yet.",
				});
			},
		},
	],
};

// ── Entries (EOI / EBN) ─────────────────────────────────────────────────────

const entries: ReportModuleDef = {
	key: "entries",
	pdfTitle: "Entries",
	filters: [],
	adminOnly: true,
	reports: [
		{
			code: "ENT_1.1",
			pdfTitle: "EOI & EBN Status by NOC",
			orientation: "landscape",
			adminOnly: true,
			async build(ctx) {
				const items: EntryOrganizationsResponse["items"] = [];
				let summary: EntryOrganizationsResponse["summary"] | null = null;
				// One page per 100 organisations; the programme has a few dozen.
				for (let page = 1; page <= 10; page++) {
					const r = await api.get<EntryOrganizationsResponse>("/entries/organizations", { query: { games_id: ctx.gamesId, page: String(page), page_size: "100", sort: "name", dir: "asc" }, signal: ctx.signal });
					items.push(...r.items);
					summary ??= r.summary;
					if (page >= r.total_pages) break;
				}
				type Org = (typeof items)[number];
				return spec<Org>({
					moduleTitle: "Entries",
					reportTitle: "EOI & EBN Status by NOC",
					code: "ENT_1.1",
					orientation: "landscape",
					subtitles: [asOfLabel(ctx.now)],
					summary: summary
						? [
								{ label: "Organisations", value: summary.organizations },
								{ label: "EOI started", value: summary.eoi_started },
								{ label: "EOI submitted", value: summary.eoi_final },
								{ label: "EBN started", value: summary.ebn_started },
								{ label: "EBN submitted", value: summary.ebn_final },
							]
						: [],
					columns: [
						{ header: "Organisation", widthPct: 22, cell: (o) => orgLabel(o.organization) },
						{ header: "EOI", widthPct: 10, cell: (o) => human(o.eoi.state) },
						{ header: "Sports", widthPct: 7, align: "center", cell: (o) => o.eoi.sports },
						{ header: "Est. Men", widthPct: 8, align: "center", cell: (o) => o.eoi.est_athletes_men },
						{ header: "Est. Women", widthPct: 8, align: "center", cell: (o) => o.eoi.est_athletes_women },
						{ header: "Est. Officials", widthPct: 9, align: "center", cell: (o) => o.eoi.est_officials },
						{ header: "EBN", widthPct: 10, cell: (o) => human(o.ebn.state) },
						{ header: "Events", widthPct: 8, align: "center", cell: (o) => o.ebn.events },
						{ header: "Entries", widthPct: 8, align: "center", cell: (o) => o.ebn.entries },
						{ header: "Athletes (implied)", align: "center", cell: (o) => o.ebn.implied_athletes },
					],
					rows: items,
					numbered: true,
					emptyText: "No organisations yet.",
				});
			},
		},
	],
};

// ── Accommodation ───────────────────────────────────────────────────────────

const accommodation: ReportModuleDef = {
	key: "accommodation",
	pdfTitle: "Accommodation",
	filters: ["org"],
	reports: [
		{
			code: "ACM_3.1",
			pdfTitle: "Rooming List",
			orientation: "landscape",
			needsOrg: true,
			async build(ctx) {
				const { items, truncated } = await fetchReportRooming(ctx.filters.org ?? "", { games_id: ctx.gamesId, sort: "room" }, ctx.signal);
				type Person = (typeof items)[number];
				return spec<Person>({
					moduleTitle: "Accommodation",
					reportTitle: "Rooming List",
					code: "ACM_3.1",
					orientation: "landscape",
					subtitles: subtitles(ctx),
					notice: truncatedNotice(truncated),
					summary: [{ label: "People", value: items.length }],
					columns: [
						{ header: "Card No", widthPct: 11, cell: (p) => (p.accreditation_no !== null ? formatAccreditationNo(p.organization_code, p.accreditation_no) : formatRegistrationNo(p.organization_code, p.registration_no)) },
						{ header: "Name", widthPct: 17, cell: (p) => p.name },
						{ header: "Gender", widthPct: 6, align: "center", cell: (p) => genderShort(p.gender) },
						{ header: "Function", widthPct: 8, cell: (p) => p.main_function?.code },
						{ header: "Team", widthPct: 9, cell: (p) => p.team },
						{ header: "State", widthPct: 8, cell: (p) => human(p.state) },
						{ header: "Building", widthPct: 12, cell: (p) => p.assignment?.building.name },
						{ header: "Room", widthPct: 6, align: "center", cell: (p) => p.assignment?.room_label },
						{ header: "Bed", widthPct: 4, align: "center", cell: (p) => p.assignment?.bed_no },
						{ header: "First Night", widthPct: 8, align: "center", cell: (p) => reportDate(p.assignment?.stay_from) },
						{ header: "Check-out", align: "center", cell: (p) => reportDate(p.assignment?.stay_to) },
					],
					rows: items,
					groupKey: (p) => p.assignment?.building.name ?? "No bed yet",
					groupLabel: (k) => `Building: ${k}`,
					numbered: true,
					emptyText: "No approved people in this contingent.",
				});
			},
		},
		{
			code: "ACM_1.1",
			pdfTitle: "Building Occupancy",
			orientation: "portrait",
			adminOnly: true,
			async build(ctx) {
				const s = await api.get<AccommodationSummary>("/accommodation/summary", { query: { games_id: ctx.gamesId }, signal: ctx.signal });
				type B = AccommodationSummary["buildings"][number];
				const pct = (b: B) => (b.beds ? `${Math.round((b.occupied_beds / b.beds) * 100)}%` : "-");
				return spec<B>({
					moduleTitle: "Accommodation",
					reportTitle: "Building Occupancy",
					code: "ACM_1.1",
					orientation: "portrait",
					subtitles: [asOfLabel(ctx.now), `Night of ${reportDate(s.date)}`],
					summary: [
						{ label: "Beds", value: s.beds },
						{ label: "Occupied", value: s.occupied_beds },
						{ label: "In house", value: s.in_house },
						{ label: "Arriving", value: s.arriving },
						{ label: "Departing", value: s.departing },
						{ label: "Without a bed", value: s.without_bed ?? "-" },
					],
					columns: [
						{ header: "Building", cell: (b) => b.name },
						{ header: "Beds", widthPct: 15, align: "center", cell: (b) => b.beds },
						{ header: "Occupied", widthPct: 15, align: "center", cell: (b) => b.occupied_beds },
						{ header: "Free", widthPct: 15, align: "center", cell: (b) => b.beds - b.occupied_beds },
						{ header: "Occupancy", widthPct: 15, align: "center", cell: pct },
					],
					rows: s.buildings,
					totals: ["Total", s.beds, s.occupied_beds, s.beds - s.occupied_beds, s.beds ? `${Math.round((s.occupied_beds / s.beds) * 100)}%` : "-"],
					numbered: true,
					emptyText: "No buildings yet.",
				});
			},
		},
	],
};

// ── Helpdesk ────────────────────────────────────────────────────────────────

const helpdesk: ReportModuleDef = {
	key: "helpdesk",
	pdfTitle: "Incident Helpdesk",
	filters: ["org", "ticketStatus", "category"],
	reports: [
		{
			code: "INC_1.1",
			pdfTitle: "Incident Tickets",
			orientation: "landscape",
			async build(ctx) {
				const { items, truncated } = await fetchReportTickets({
					games_id: ctx.gamesId,
					responsible_organization_id: ctx.filters.org,
					// The list's own default is open tickets only; a report of "every status" asks for all three.
					status: (ctx.filters.ticketStatus ?? "OPEN,IN_PROGRESS,RESOLVED") as never,
					category: ctx.filters.category as never,
				}, ctx.signal);
				const rows = groupedBy(items, (t) => t.category);
				const count = (status: string) => items.filter((t) => t.status === status).length;
				return spec<(typeof items)[number]>({
					moduleTitle: "Incident Helpdesk",
					reportTitle: "Incident Tickets",
					code: "INC_1.1",
					orientation: "landscape",
					subtitles: subtitles(ctx, [
						["Status", ctx.filters.ticketStatus ? human(ctx.filters.ticketStatus) : null],
						["Category", ctx.filters.category ? human(ctx.filters.category) : null],
					]),
					notice: truncatedNotice(truncated),
					summary: [
						{ label: "Tickets", value: items.length },
						{ label: "Open", value: count("OPEN") },
						{ label: "In progress", value: count("IN_PROGRESS") },
						{ label: "Resolved", value: count("RESOLVED") },
					],
					columns: [
						{ header: "Ticket No", widthPct: 9, cell: (t) => formatTicketNo(t.ticket_no) },
						{ header: "Priority", widthPct: 7, cell: (t) => human(t.priority) },
						{ header: "Issue", widthPct: 24, cell: (t) => t.title },
						{ header: "Org.", widthPct: 6, align: "center", cell: (t) => t.organization?.code ?? "-" },
						{ header: "Building / Room", widthPct: 14, cell: (t) => [t.building?.name, t.room?.room_label].filter(Boolean).join(" / ") },
						{ header: "Assigned To", widthPct: 11, cell: (t) => t.assignee?.name },
						{ header: "Status", widthPct: 8, cell: (t) => human(t.status) },
						{ header: "Created", widthPct: 9, align: "center", cell: (t) => reportDateTime(t.created_at) },
						{ header: "Last Updated", align: "center", cell: (t) => reportDateTime(t.updated_at) },
					],
					rows,
					groupKey: (t) => human(t.category),
					groupLabel: (k) => `Category: ${k} (${items.filter((t) => human(t.category) === k).length})`,
					emptyText: "No tickets match these filters.",
				});
			},
		},
	],
};

export const REPORT_MODULES: readonly ReportModuleDef[] = [accreditation, eAccreditation, entries, accommodation, helpdesk];

export const moduleBySlug = (slug: string): ReportModuleDef | undefined => REPORT_MODULES.find((m) => REPORT_MODULE_SLUGS[m.key] === slug);
